/**
 * QuickLayoutMenu — what to put in a bare area, in one click.
 *
 * Opens from the "+" on a bare area of the locked surface. A row of tiles, each
 * a small picture of the wall the layout makes — white and gray bands, board
 * lines running the way the boards will, SlatWall drawn as slats — so the
 * choice reads at a glance. Choosing one adds all of its sections at once.
 *
 * Layouts whose bands would not fit the area (a 33/33/33 stack in a strip two
 * feet tall) are shown greyed out, with the reason in their tooltip.
 */

import React, { useEffect, useRef } from 'react';
import { QuickLayout, QuickLayoutBand, QuickLayoutId } from '../../calculations/sections';
import { DESIGNER_STRINGS as S } from './strings';

export interface QuickLayoutChoice {
  layout: QuickLayout;
  /** False when the layout's bands would not fit the area. */
  fits: boolean;
}

export interface QuickLayoutMenuProps {
  /** Anchor — the centre of the "+" — in viewer px. */
  x: number;
  y: number;
  choices: QuickLayoutChoice[];
  onChoose: (id: QuickLayoutId) => void;
  onClose: () => void;
}

const AMBER = '#FEB100';
const TILE = 64;
const GAP = 6;
const PAD = 8;

/** The product colours as they are drawn: white is white, gray is Trusscore's gray. */
const FILL: Record<QuickLayoutBand['color'], string> = { White: '#ffffff', Gray: '#D0D3D4' };

/** Board or slat lines, dark enough to read on both colours. */
const LINE = 'rgba(0,0,0,0.2)';

/**
 * A picture of the layout: a wall 36 × 40 with its bands drawn to their shares.
 * Board bands show their joint lines (vertical boards → vertical lines); a
 * SlatWall band shows slats — a tight run of horizontal grooves.
 */
const LayoutGlyph: React.FC<{ layout: QuickLayout }> = ({ layout }) => {
  const W = 36, H = 40, R = 3;
  const total = layout.bands.reduce((t, b) => t + b.share, 0);
  let y = 0;
  const bands = layout.bands.map((band, i) => {
    const h = (H * band.share) / total;
    const top = y;
    y += h;
    const lines: React.ReactElement[] = [];
    if (band.productType === 'SlatWall') {
      // Slats: a groove every 4px, drawn as a line with a lighter line under it.
      for (let g = top + 4; g < top + h - 1; g += 4) {
        lines.push(<line key={`g${g}`} x1={0} y1={g} x2={W} y2={g} stroke={LINE} strokeWidth={1.4} />);
      }
    } else if (band.orientation === 'Vertical') {
      for (let x = 9; x < W; x += 9) {
        lines.push(<line key={`v${x}`} x1={x} y1={top} x2={x} y2={top + h} stroke={LINE} strokeWidth={1} />);
      }
    } else {
      for (let g = top + 8; g < top + h - 1; g += 8) {
        lines.push(<line key={`h${g}`} x1={0} y1={g} x2={W} y2={g} stroke={LINE} strokeWidth={1} />);
      }
    }
    return (
      <g key={i}>
        <rect x={0} y={top} width={W} height={h} fill={FILL[band.color]} />
        {lines}
        {/* The join between bands */}
        {i > 0 && <line x1={0} y1={top} x2={W} y2={top} stroke="rgba(0,0,0,0.45)" strokeWidth={1} />}
      </g>
    );
  });
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" style={{ display: 'block' }}>
      <defs>
        <clipPath id={`ql-clip-${layout.id}`}><rect x={0} y={0} width={W} height={H} rx={R} /></clipPath>
      </defs>
      <g clipPath={`url(#ql-clip-${layout.id})`}>{bands}</g>
      <rect x={0.5} y={0.5} width={W - 1} height={H - 1} rx={R} fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth={1} />
    </svg>
  );
};

export const QuickLayoutMenu: React.FC<QuickLayoutMenuProps> = ({ x, y, choices, onChoose, onClose }) => {
  const width = PAD * 2 + choices.length * TILE + (choices.length - 1) * GAP;
  // A press anywhere outside the menu dismisses it.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={S.quickLayoutTitle}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="designer-quick-layouts"
      style={{
        position: 'absolute', zIndex: 8, width,
        // Centred under the "+", kept inside the viewer.
        left: `clamp(8px, ${x - width / 2}px, calc(100% - ${width + 8}px))`,
        top: `clamp(8px, ${y + 22}px, calc(100% - 120px))`,
        background: 'rgba(26, 26, 30, 0.97)', color: '#fff', borderRadius: 10, padding: PAD,
        border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
        pointerEvents: 'auto', userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#a9a9b4' }}>
        {S.quickLayoutTitle}
        <button type="button" onClick={onClose} aria-label={S.close} style={{ background: 'none', border: 'none', color: '#a9a9b4', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2 }}>×</button>
      </div>
      <div style={{ display: 'flex', gap: GAP }}>
        {choices.map(({ layout, fits }) => {
          const name = S.quickLayout[layout.id];
          return (
            <button
              key={layout.id}
              type="button"
              role="menuitem"
              title={fits ? S.quickLayoutHint[layout.id] : S.quickLayoutTooSmall}
              aria-label={name}
              disabled={!fits}
              onClick={() => onChoose(layout.id)}
              className="designer-quick-layout"
              style={{
                width: TILE, padding: '7px 4px 5px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                color: '#e4e4ea', cursor: fits ? 'pointer' : 'not-allowed', opacity: fits ? 1 : 0.35,
              }}
            >
              <LayoutGlyph layout={layout} />
              <span style={{ fontSize: 10, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'normal' }}>{name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuickLayoutMenu;
